import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService, { User, LoginCredentials, RegisterData } from '../../services/authService';
import { uploadAvatar as uploadProfileAvatar } from './settingsSlice';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  loginLoading: boolean;
  registerLoading: boolean;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  loginLoading: false,
  registerLoading: false,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const authData = await authService.login(credentials);
      
      // Store user data in localStorage for easy access
      localStorage.setItem('user_data', JSON.stringify(authData.data.user));
      authService.saveTokensToStorage(authData.data.access_token, authData.data.refresh_token);
      
      return authData;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (data: RegisterData, { rejectWithValue }) => {
    try {
      const authData = await authService.register(data);
      
      // Store user data in localStorage for easy access
      localStorage.setItem('user_data', JSON.stringify(authData.data.user));
      
      return authData;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      // Clear user data from localStorage
      localStorage.removeItem('user_data');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Logout failed');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      // First try to get user data from localStorage
      const storedUserData = localStorage.getItem('user_data');
      if (storedUserData) {
        const user = JSON.parse(storedUserData);
        // Validate that the stored user data is still valid by checking token
        const token = authService.getAccessToken();
        if (token) {
          return user;
        } else {
          // Token is invalid, clear stored data and proceed to API
          localStorage.removeItem('user_data');
        }
      }
      
      // If not in localStorage or token invalid, fetch from API
      const user = await authService.getCurrentUser();
      localStorage.setItem('user_data', JSON.stringify(user));
      return user;
    } catch (error: any) {
      // Clear invalid session data
      localStorage.removeItem('user_data');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expiry');
      return rejectWithValue(error.response?.data?.message || 'Failed to get current user');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data: Partial<User>, { rejectWithValue }) => {
    try {
      const updatedUser = await authService.updateProfile(data);
      
      // Update user data in localStorage
      localStorage.setItem('user_data', JSON.stringify(updatedUser));
      
      return updatedUser;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Profile update failed');
    }
  }
);

// Utility function to get current user data from localStorage
export const getCurrentUserFromStorage = (): User | null => {
  try {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing user data from localStorage:', error);
    return null;
  }
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    setAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loginLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.loginLoading = false;
        state.user = action.payload.data.user;
      })
      .addCase(login.rejected, (state, action) => {
        state.loginLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })
      
      // Register
      .addCase(register.pending, (state) => {
        state.registerLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.registerLoading = false;
        state.user = action.payload.data.user;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.registerLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })
      
      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.loginLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.loginLoading = false;
        state.error = action.payload as string;
      })
      
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })
      
      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Upload avatar — reacts to settingsSlice's uploadAvatar (the one
      // actually dispatched by Settings.tsx) so the sidebar/header avatar
      // updates immediately without a full page reload/re-login. This
      // used to be its own separate thunk hitting a different, dead
      // endpoint (authService.uploadAvatar -> /auth/upload-avatar) that
      // nothing in the UI ever called.
      .addCase(uploadProfileAvatar.fulfilled, (state, action) => {
        if (state.user) {
          // Backend has been observed returning either key.
          state.user.avatar_url = action.payload.avatar_url ?? action.payload.avatar;
          localStorage.setItem('user_data', JSON.stringify(state.user));
        }
      });
  },
});

export const { clearError, clearAuth, setAuthenticated } = authSlice.actions;
export default authSlice.reducer;
